/**
 * What the step-up dialog restates, and how it retries (design doc §20.2,
 * §22.3; ADR-043).
 *
 * The shared HTTP seam reports a `401 REAUTHENTICATION_REQUIRED` with the
 * refused request's method, path and body. The path names the contract
 * operation (`routeRequirement`), and {@link STEP_UP_ACTIONS} names the
 * action for each of the server's step-up routes; the people, connectors and
 * brands it concerns are read from what the screen already cached. The
 * account and workspace come from the cached session (`GET /me`).
 *
 * The retry is the refused mutation itself, and only that one (ADR-049).
 * Every refusal carries its own id ({@link AuthFailureContext.refusalId}),
 * and so does the error its request rejects with; the mutation that fails
 * with that error is the one to re-run, found from the mutation cache's own
 * error event — never guessed as "the latest pending", which picks the wrong
 * one when two step-up mutations are in flight. Refusals wait in a queue and
 * share one prompt: one fresh sign-in re-runs every bound mutation, each once,
 * in the order they were refused (ADR-056). A refusal no mutation claimed — a
 * request made outside a mutation, or one whose `mutationFn` threw a
 * different error without the refused one as its `cause` — is listed as "not
 * retried": nothing is re-sent for it, and the dialog says which of the two
 * it was ({@link StepUpEntry.rethrown}, ADR-062).
 *
 * "Not now" no longer drops anything (ADR-062): the refusals move to a
 * deferred list ({@link deferStepUps}) that stays on screen, each with its
 * own "Retry sign-in" ({@link takeDeferredStepUp}) and "Discard"
 * ({@link discardDeferredStepUp}). Nothing in it is ever sent without a
 * fresh sign-in the person asked for.
 *
 * The list survives a reload (ADR-068, `stepUpStore.ts`). What is kept of a
 * refused request is {@link StepUpRequest}: its method, path, the
 * `Idempotency-Key` and `If-Match` it carried, and a body only when every
 * field is on that action's list in {@link STEP_UP_BODY_FIELDS} — role and
 * user ids, an automation level — never anything else. Three actions are
 * never sent again after a reload ({@link STEP_UP_ONE_TIME_ANSWER}): their
 * answer (a signing secret, a provider link) can only be used by the page
 * that asked.
 */
import type { Mutation, QueryClient } from '@tanstack/react-query';
import type { components } from '@cedarreachai/api-client';
import type { AuthFailureContext } from '@/lib/api/http';
import { queryKeys, sessionKeys, workspaceScope } from '@/lib/queryKeys';
import { routeRequirement, type MutatingRoute } from './capabilities';

type Schemas = components['schemas'];

/**
 * Completes a fresh sign-in without leaving the page, resolving once the
 * session is refreshed. Only the mock build registers one (MSW cannot answer
 * a navigation); a real deployment navigates to `/auth/login?reauth=1`.
 */
export type StepUpReauthenticator = (returnTo: string) => Promise<void>;

let registered: StepUpReauthenticator | null = null;

/** Registers (or with `null`, removes) the in-place re-authentication. Mock build only. */
export function setStepUpReauthenticator(reauthenticate: StepUpReauthenticator | null): void {
  registered = reauthenticate;
}

/** The registered in-place re-authentication, if any. */
export function stepUpReauthenticator(): StepUpReauthenticator | null {
  return registered;
}

/** Any mutation in the cache, whatever its types. */
export type AnyMutation = Mutation<unknown, unknown, unknown, unknown>;

/** One refused request waiting for its prompt. */
export interface StepUpEntry {
  /** The refusal's id: {@link AuthFailureContext.refusalId}. */
  readonly id: string;
  readonly account: string | null;
  readonly workspace: string | null;
  /** The workspace and brand ids the refused request was scoped to, when known. */
  readonly workspaceId?: string | null;
  readonly brandId?: string | null;
  readonly action: StepUpAction;
  /**
   * The mutation the refused request belonged to — the one re-run after a
   * fresh sign-in — once its error has reached the mutation cache; `null`
   * for a request made outside a mutation (nothing is re-sent).
   */
  readonly mutation: AnyMutation | null;
  /**
   * True when no mutation claimed this refusal but a mutation that was in
   * flight when it arrived then failed with an error that does not carry it
   * (a `mutationFn` that rethrew a different error without `cause`). Told
   * apart from a request outside any mutation so the prompt says which; it is
   * still not re-sent, because the page cannot prove which mutation the
   * refused request belonged to (ADR-062).
   */
  readonly rethrown?: boolean;
  /** When the refusal arrived (ms since the epoch); expiry is measured from it. */
  readonly createdAt?: number;
  /**
   * What may be sent again after a reload ({@link stepUpRequestOf}); `null`
   * when this action is never re-sent from storage.
   */
  readonly request?: StepUpRequest | null;
  /**
   * Restored from storage after a reload: there is no mutation to re-run,
   * only {@link request}, sent once with the same key. `bound` records
   * whether a mutation had claimed the refusal before the reload — the only
   * proof the request was a mutation's own.
   */
  readonly restored?: { readonly bound: boolean };
}

/**
 * A refused request as it is kept across a reload (ADR-068). Never a secret:
 * the body is kept only when every field is on its action's list.
 */
export interface StepUpRequest {
  readonly method: string;
  readonly path: string;
  readonly body?: Readonly<Record<string, string>>;
  readonly idempotencyKey?: string;
  readonly ifMatchVersion?: string;
}

/**
 * Actions whose answer only the page that asked can use — a webhook's
 * signing secret shown once, a provider's sign-in link to follow — and so
 * are never sent again from storage after a reload.
 */
export const STEP_UP_ONE_TIME_ANSWER: ReadonlySet<ActionKey> = new Set<ActionKey>([
  'addConnector',
  'rotateKey',
  'reconnect',
]);

/**
 * The body fields each re-sendable step-up action may keep across a reload;
 * an action absent here sends no body. Each value must be a short string.
 * A body with any other field is not kept, and the action is not re-sent.
 */
export const STEP_UP_BODY_FIELDS: Readonly<Partial<Record<ActionKey, readonly string[]>>> = {
  editWorkspace: ['automation_level'],
  automationLevel: ['automation_level'],
  addMember: ['user_id', 'role'],
  changeRole: ['role'],
};

/** The longest body value kept (role names, UUIDs and levels are far shorter). */
const MAX_KEPT_VALUE = 64;

/**
 * What of the refused request may be kept and sent again after a reload,
 * or `null` when nothing may: an action outside {@link STEP_UP_ACTIONS}, one
 * in {@link STEP_UP_ONE_TIME_ANSWER}, or a body with a field (or a value)
 * its action does not list.
 */
export function stepUpRequestOf(
  context:
    | Pick<AuthFailureContext, 'method' | 'path' | 'body' | 'idempotencyKey' | 'ifMatchVersion'>
    | undefined,
): StepUpRequest | null {
  if (!context) return null;
  const requirement = routeRequirement(context.method, context.path);
  const action = requirement ? STEP_UP_ACTIONS[requirement.route] : undefined;
  if (!action || STEP_UP_ONE_TIME_ANSWER.has(action)) return null;
  const allowed = STEP_UP_BODY_FIELDS[action] ?? [];
  let body: Record<string, string> | undefined;
  if (context.body !== undefined && context.body !== null) {
    if (typeof context.body !== 'object' || Array.isArray(context.body)) return null;
    body = {};
    for (const [field, value] of Object.entries(context.body as Record<string, unknown>)) {
      if (value === undefined) continue;
      if (!allowed.includes(field)) return null;
      if (typeof value !== 'string' || value.length > MAX_KEPT_VALUE) return null;
      body[field] = value;
    }
  }
  return {
    method: context.method,
    path: context.path,
    ...(body ? { body } : {}),
    ...(context.idempotencyKey ? { idempotencyKey: context.idempotencyKey } : {}),
    ...(context.ifMatchVersion ? { ifMatchVersion: context.ifMatchVersion } : {}),
  };
}

/** Why a waiting action cannot be sent again; `null` when it can. */
export type StepUpBlock =
  'expired' | 'rethrown' | 'one_time_answer' | 'not_kept' | 'outside_mutation';

/**
 * Whether "Retry sign-in" may send `entry` again, judged at `now` with keys
 * that live `lifetimeMs`: an action older than the server keeps its key is
 * expired (sending it could run it twice); a restored action is re-sent only
 * when a mutation had claimed it and it kept its request; a rethrown one
 * never is. A live, unexpired entry is never blocked here — its prompt says
 * what will and will not be retried, as before (ADR-062).
 */
export function stepUpBlock(
  entry: StepUpEntry,
  now: number,
  lifetimeMs: number,
): StepUpBlock | null {
  if (entry.createdAt !== undefined && now - entry.createdAt >= lifetimeMs) return 'expired';
  if (!entry.restored) return null;
  if (entry.rethrown) return 'rethrown';
  if (!entry.request) {
    const action = entry.action.key.slice('auth.stepUp.action.'.length) as ActionKey;
    return STEP_UP_ONE_TIME_ANSWER.has(action) ? 'one_time_answer' : 'not_kept';
  }
  if (!entry.restored.bound) return 'outside_mutation';
  return null;
}

/** `queue` with `entry` last, unless a refusal with its id is already waiting. */
export function enqueueStepUp(
  queue: readonly StepUpEntry[],
  entry: StepUpEntry,
): readonly StepUpEntry[] {
  return queue.some((e) => e.id === entry.id) ? queue : [...queue, entry];
}

/**
 * `queue` with `mutation` bound to the refusal `refusalId`: the mutation
 * whose request was refused, as its error says. No other entry changes.
 */
export function bindStepUpMutation(
  queue: readonly StepUpEntry[],
  refusalId: string,
  mutation: AnyMutation,
): readonly StepUpEntry[] {
  return queue.some((e) => e.id === refusalId)
    ? queue.map((e) => (e.id === refusalId ? { ...e, mutation } : e))
    : queue;
}

/**
 * `queue` with every unclaimed entry among `refusalIds` marked
 * {@link StepUpEntry.rethrown}. A bound entry is left as it is: its mutation
 * carried the refusal, which is proof; this is only a reading.
 */
export function markStepUpRethrown(
  queue: readonly StepUpEntry[],
  refusalIds: ReadonlySet<string>,
): readonly StepUpEntry[] {
  return queue.some((e) => refusalIds.has(e.id) && e.mutation === null && !e.rethrown)
    ? queue.map((e) =>
        refusalIds.has(e.id) && e.mutation === null && !e.rethrown ? { ...e, rethrown: true } : e,
      )
    : queue;
}

/**
 * "Not now": `deferred` with every entry of `queue` appended in refusal
 * order, none twice. Nothing is sent and nothing is dropped.
 */
export function deferStepUps(
  deferred: readonly StepUpEntry[],
  queue: readonly StepUpEntry[],
): readonly StepUpEntry[] {
  return queue.reduce(enqueueStepUp, deferred);
}

/**
 * "Retry sign-in" on one deferred refusal: that entry (to prompt for again)
 * and the list without it; `null` and the list unchanged for an unknown id.
 */
export function takeDeferredStepUp(
  deferred: readonly StepUpEntry[],
  refusalId: string,
): { readonly entry: StepUpEntry | null; readonly rest: readonly StepUpEntry[] } {
  const entry = deferred.find((e) => e.id === refusalId) ?? null;
  return { entry, rest: entry ? deferred.filter((e) => e.id !== refusalId) : deferred };
}

/** "Discard" on one deferred refusal: the list without it. Nothing is sent. */
export function discardDeferredStepUp(
  deferred: readonly StepUpEntry[],
  refusalId: string,
): readonly StepUpEntry[] {
  return deferred.filter((e) => e.id !== refusalId);
}

type ActionKey =
  | 'changeRole'
  | 'suspend'
  | 'reinstate'
  | 'addMember'
  | 'resume'
  | 'editWorkspace'
  | 'automationLevel'
  | 'addConnector'
  | 'revokeConnector'
  | 'rotateKey'
  | 'reconnect';

/**
 * The server's step-up routes (`STEP_UP_ROUTES` in `services/api/src/auth.rs`)
 * and the action each one is, in the dialog's words. A test keeps the two
 * lists equal through the mock, which enforces exactly these routes.
 */
export const STEP_UP_ACTIONS: Readonly<Partial<Record<MutatingRoute, ActionKey>>> = {
  'POST /api/v1/workspaces/{workspace_id}/resume': 'resume',
  'PATCH /api/v1/workspaces/{workspace_id}': 'editWorkspace',
  'PUT /api/v1/workspaces/{workspace_id}/brands/{brand_id}/automation-level': 'automationLevel',
  'POST /api/v1/workspaces/{workspace_id}/connectors': 'addConnector',
  'DELETE /api/v1/workspaces/{workspace_id}/connectors/{connector_id}': 'revokeConnector',
  'POST /api/v1/workspaces/{workspace_id}/connectors/{connector_id}/signing-keys:rotate':
    'rotateKey',
  'POST /api/v1/workspaces/{workspace_id}/connectors/{connector_id}/authorize': 'reconnect',
  'POST /api/v1/workspaces/{workspace_id}/members': 'addMember',
  'PATCH /api/v1/workspaces/{workspace_id}/members/{membership_id}': 'changeRole',
  'POST /api/v1/workspaces/{workspace_id}/members/{membership_id}/suspend': 'suspend',
  'POST /api/v1/workspaces/{workspace_id}/members/{membership_id}/reinstate': 'reinstate',
};

/** The step-up routes, as contract operations. */
export const STEP_UP_ROUTES = Object.keys(STEP_UP_ACTIONS) as readonly MutatingRoute[];

/** An i18n key under `auth.stepUp.action` with its ICU values. */
export interface StepUpAction {
  readonly key: `auth.stepUp.action.${ActionKey | 'other'}`;
  readonly values: Readonly<Record<string, string>>;
  /** A role whose name the dialog translates (`settings.members.roles.*`). */
  readonly role?: Schemas['Role'];
}

/** The workspace in the page's path (`/w/{id}/…`), if any. */
export function workspaceIdFromPath(pathname: string): string | null {
  const match = /^\/w\/([^/]+)/.exec(pathname);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/** Who is signed in and in which workspace, from the cached session. */
export function stepUpScope(
  client: QueryClient | undefined,
  workspaceId: string | null,
): { readonly account: string | null; readonly workspace: string | null } {
  const session = client?.getQueryData<Schemas['Session']>(sessionKeys.me());
  const membership = workspaceId
    ? session?.memberships.find((m) => m.workspace_id === workspaceId)
    : undefined;
  return {
    account: session?.user.display_name ?? null,
    workspace: membership?.workspace_name ?? workspaceId,
  };
}

/** The rows a cached read holds: a list, a page, or pages of either. */
function rowsOf(data: unknown): readonly Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (typeof data !== 'object' || data === null) return [];
  const record = data as Record<string, unknown>;
  if (Array.isArray(record['pages'])) return record['pages'].flatMap(rowsOf);
  if (Array.isArray(record['items'])) return record['items'] as Record<string, unknown>[];
  return [record];
}

/**
 * The display name of the member, connector or brand `id`, from whatever the
 * screen has cached for this workspace; the id itself when nothing is.
 */
function cachedName(
  client: QueryClient | undefined,
  workspaceId: string | null,
  idField: 'membership_id' | 'id',
  id: string,
): string {
  if (!client || !workspaceId) return id;
  const prefixes =
    idField === 'membership_id'
      ? [queryKeys.members(workspaceId)]
      : [
          queryKeys.connectors(workspaceId),
          queryKeys.brands(workspaceId),
          workspaceScope(workspaceId),
        ];
  for (const queryKey of prefixes) {
    for (const [, data] of client.getQueriesData<unknown>({ queryKey })) {
      const row = rowsOf(data).find((r) => r[idField] === id);
      const name = row?.['display_name'] ?? row?.['name'];
      if (typeof name === 'string' && name.length > 0) return name;
    }
  }
  return id;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

/**
 * Which step-up action the refused request was: one of the server's step-up
 * routes by name, with the person, connector or brand it concerns; any other
 * request as "the change you just confirmed".
 */
export function describeStepUpAction(
  context: Pick<AuthFailureContext, 'method' | 'path' | 'body'> | undefined,
  client: QueryClient | undefined,
): StepUpAction {
  const other: StepUpAction = { key: 'auth.stepUp.action.other', values: {} };
  if (!context) return other;
  const requirement = routeRequirement(context.method, context.path);
  const action = requirement ? STEP_UP_ACTIONS[requirement.route] : undefined;
  if (!requirement || !action) return other;
  const workspaceId = requirement.workspaceId;
  const params = requirement.params;
  const body =
    typeof context.body === 'object' && context.body !== null
      ? (context.body as Record<string, unknown>)
      : {};
  const key = `auth.stepUp.action.${action}` as const;
  const role = str(body['role']) as Schemas['Role'] | null;
  const member = () =>
    cachedName(client, workspaceId, 'membership_id', params['membership_id'] ?? '');
  const named = (id: string | undefined) => cachedName(client, workspaceId, 'id', id ?? '');
  switch (action) {
    case 'changeRole':
      return role ? { key, values: { name: member() }, role } : other;
    case 'suspend':
    case 'reinstate':
      return { key, values: { name: member() } };
    case 'addMember':
      return role ? { key, values: { userId: str(body['user_id']) ?? '' }, role } : other;
    case 'automationLevel':
      return { key, values: { name: named(params['brand_id']) } };
    case 'addConnector':
      return {
        key,
        values: { name: str(body['display_name']) ?? str(body['provider']) ?? '' },
      };
    case 'revokeConnector':
    case 'rotateKey':
    case 'reconnect':
      return { key, values: { name: named(params['connector_id']) } };
    case 'resume':
    case 'editWorkspace':
      return { key, values: {} };
  }
}
